import { delay, putResolve, select } from "redux-saga/effects";
import pollPriceQuote from "../../../../api/v0/book/book-flow/pollPriceQuote";
import { IStoreState } from "../../../../reducers/types";
import { getSession, getPriceQuoteVRProperties } from "../../reducer";
import { HomesPriceQuote, PRICE_QUOTE_VR } from "redmond";
import {
  setPollPriceQuoteCallStateSuccess,
  setPollVacationRentalsPriceQuoteCallStateFailure,
  setVacationRentalsPriceQuote,
} from "../../actions/actions";
import { actions } from "../../actions";
import {
  Product,
  QuoteFailure,
  QuoteResponseEnum,
  QuoteSuccessV0,
  QuoteResponseV0,
} from "@b2bportal/purchase-api";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import dayjs from "dayjs";

export function* pollVacationRentalsPriceQuoteSaga({
  pollQuoteOnly,
}: actions.IPollVacationRentalsPriceQuote) {
  try {
    const state: IStoreState = yield select();
    const sessionToken = getSession(state);
    const startTime = dayjs();
    const priceQuoteVRProperties = getPriceQuoteVRProperties(state);

    if (!sessionToken) {
      throw new Error("Session token is not present.");
    }

    const delayTimes = [1000];

    let pollFailed = false;
    let index = 0;
    while (!pollFailed) {
      yield delay(delayTimes[index]);
      const priceQuoteCheckedResponse: QuoteResponseV0 = yield pollPriceQuote(
        sessionToken
      );

      switch (priceQuoteCheckedResponse.QuoteResponse) {
        case QuoteResponseEnum.Failure:
          pollFailed = true;
          const failedResponse = priceQuoteCheckedResponse as QuoteFailure;
          if (failedResponse.errors.length > 0) {
            yield putResolve(
              setPollVacationRentalsPriceQuoteCallStateFailure(
                failedResponse.errors
              )
            );
            trackEvent({
              eventName: PRICE_QUOTE_VR,
              properties: {
                ...priceQuoteVRProperties.properties,
                failure_reason: "Poll quote checked response returned an error",
              },
              encryptedProperties: [
                ...priceQuoteVRProperties.encryptedProperties,
              ],
            });
            return;
          } else {
            trackEvent({
              eventName: PRICE_QUOTE_VR,
              properties: {
                ...priceQuoteVRProperties.properties,
                failure_reason:
                  "Vacation Rentals price quote checked response returned an error and the given error code is not handleable.",
              },
              encryptedProperties: [
                ...priceQuoteVRProperties.encryptedProperties,
              ],
            });
            yield putResolve(
              setPollVacationRentalsPriceQuoteCallStateFailure([])
            );
            throw new Error(
              "Vacation Rentals price quote checked response returned an error and the given error code is not handleable."
            );
          }

        case QuoteResponseEnum.Pending:
          break;
        case QuoteResponseEnum.Success:
          const priceQuoteResponse =
            priceQuoteCheckedResponse as QuoteSuccessV0;
          switch (priceQuoteResponse.quote.type) {
            case Product.Homes:
              // BE returns a HotelPriceQuoteWithAncillaries object
              // If hotels cfar is not enabled, the hotelAncillaryQuotes and combinedPricing fields will be empty
              const priceQuote = priceQuoteResponse.quote
                .value as HomesPriceQuote;

              yield putResolve(setPollPriceQuoteCallStateSuccess());
              yield putResolve(setVacationRentalsPriceQuote(priceQuote));

              trackEvent({
                eventName: PRICE_QUOTE_VR,
                properties: {
                  ...priceQuoteVRProperties.properties,
                  success: true,
                  load_time: dayjs().diff(startTime, "seconds", true),
                },
                encryptedProperties: [
                  ...priceQuoteVRProperties.encryptedProperties,
                ],
              });

              if (pollQuoteOnly) {
                return;
              }
              return;
            default:
              pollFailed = true;
              trackEvent({
                eventName: PRICE_QUOTE_VR,
                properties: {
                  ...priceQuoteVRProperties.properties,
                  failure_reason: "Vacation Rentals Price Quote Failed",
                },
                encryptedProperties: [
                  ...priceQuoteVRProperties.encryptedProperties,
                ],
              });
              yield putResolve(
                setPollVacationRentalsPriceQuoteCallStateFailure([])
              );
              throw new Error("Vacation Rentals Price Quote Failed");
          }
      }
      if (index !== delayTimes.length - 1) {
        index++;
      }
    }
  } catch (_e) {
    const state: IStoreState = yield select();
    const priceQuoteVRProperties = getPriceQuoteVRProperties(state);

    trackEvent({
      eventName: PRICE_QUOTE_VR,
      properties: {
        ...priceQuoteVRProperties.properties,
        failure_reason: "Poll quote request failed",
      },
      encryptedProperties: [
        ...priceQuoteVRProperties.encryptedProperties,
      ],
    });
    yield putResolve(setPollVacationRentalsPriceQuoteCallStateFailure([]));
  }
}
